import { useEffect, useState } from 'react';
import type { CountryCode } from 'libphonenumber-js';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  detectCountry,
  formatForDisplay,
  nationalToE164,
  parsePhoneInput,
} from '@/utils/phone';

interface PhoneInputProps {
  value: string; // Format E.164: "+33606060606"
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
  dataTestId?: string;
}

export function PhoneInput({
  value,
  onChange,
  label = 'Téléphone',
  required,
  error,
  className = '',
  dataTestId,
}: PhoneInputProps) {
  const [country, setCountry] = useState<CountryCode>(() => detectCountry(value) ?? DEFAULT_COUNTRY);
  const [display, setDisplay] = useState<string>(() => formatForDisplay(value, country));

  useEffect(() => {
    const incomingCountry = detectCountry(value);
    if (incomingCountry && incomingCountry !== country) {
      setCountry(incomingCountry);
    }
    if (value && nationalToE164(display, country) !== value) {
      setDisplay(formatForDisplay(value, incomingCountry ?? country));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleCountryChange = (code: CountryCode) => {
    setCountry(code);
    onChange(nationalToE164(display, code));
  };

  const handleInputChange = (input: string) => {
    const { display: nextDisplay, e164, country: nextCountry } = parsePhoneInput(input, country);
    setDisplay(nextDisplay);
    if (nextCountry !== country) setCountry(nextCountry);
    onChange(e164);
  };

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </label>
      )}
      <div className="tw-flex">
        <select
          value={country}
          onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
          className="tw-px-2 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-l-lg tw-border-r-0 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary tw-bg-white"
          style={{ maxWidth: '130px' }}
          data-testid={dataTestId ? `${dataTestId}-country` : undefined}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dialCode}
            </option>
          ))}
        </select>
        <input
          type="tel"
          value={display}
          onChange={(e) => handleInputChange(e.target.value)}
          required={required}
          className={`tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-r-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary ${
            error ? 'tw-border-red-500' : 'tw-border-gray-300'
          }`}
          data-testid={dataTestId}
          autoComplete="tel"
        />
      </div>
      {error && <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>}
    </div>
  );
}
